import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, MapPin, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, Reveal, SectionShell } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  Professional: "bg-volt text-ink",
  "Emerging Pro": "bg-[#10B981] text-ink",
  Amateur: "bg-[#FACC15] text-ink",
  "Youth / NIL": "bg-[#2563EB] text-white",
};

function AthleteCard({ athlete, index }) {
  const highlights = (athlete.highlights || "")
    .split(";")
    .map((h) => h.trim())
    .filter(Boolean);

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/athlete/${athlete.id}`);
      toast.success("Profile link copied to clipboard.");
    } catch {
      toast.error("Could not copy the link — open the profile to copy it from the address bar.");
    }
  };

  return (
    <Reveal delay={0.06 * index} className="h-full">
      <Link
        to={`/athlete/${athlete.id}`}
        onClick={() => window.gtag?.("event", "select_content", { content_type: "athlete_profile", item_id: athlete.id, item_name: athlete.name })}
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-volt/50 hover:shadow-[0_20px_60px_rgba(132,204,22,0.1)]"
        data-testid={`roster-athlete-card-${athlete.id}`}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-elev">
          {athlete.photo_path ? (
            <img
              src={`${API}/files/${athlete.photo_path}`}
              alt={athlete.name}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              data-testid={`roster-athlete-photo-${athlete.id}`}
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-elev to-ink">
              <span className="font-display text-7xl text-volt/60">
                {athlete.name.slice(0, 1).toUpperCase()}
              </span>
            </div>
          )}
          <span
            className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
              LEVEL_STYLES[athlete.level] || "bg-volt text-ink"
            }`}
            data-testid={`roster-athlete-level-${athlete.id}`}
          >
            {athlete.level}
          </span>
          <button
            onClick={share}
            data-testid={`roster-athlete-share-btn-${athlete.id}`}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-ink/70 text-slate-300 backdrop-blur-md transition-colors hover:border-volt hover:text-volt"
            aria-label="Copy profile link"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-display text-2xl uppercase text-white">{athlete.name}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-slate-400">
            {athlete.ranking && (
              <span className="inline-flex items-start gap-1.5 whitespace-pre-line text-volt">
                <Trophy className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {athlete.ranking}
              </span>
            )}
            {athlete.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {athlete.location}
              </span>
            )}
          </div>

          {athlete.bio && (
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{athlete.bio}</p>
          )}

          {highlights.length > 0 && (
            <ul className="mt-4 space-y-1.5 border-t border-white/10 pt-4">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-xs text-slate-300">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />
                  {h}
                </li>
              ))}
            </ul>
          )}

          <span className="mt-auto inline-flex items-center gap-2 pt-5 font-mono text-[11px] uppercase tracking-wider text-volt">
            View Profile
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function RosterGallery() {
  const [athletes, setAthletes] = useState(null);

  useEffect(() => {
    fetch(`${API}/roster`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setAthletes)
      .catch(() => setAthletes([]));
  }, []);

  return (
    <SectionShell id="gallery" className="bg-surface/40">
      <Reveal>
        <Eyebrow className="mb-5">— The Roster</Eyebrow>
        <h2 className="max-w-3xl font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
          Signed <span className="text-volt">athletes.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          The players building the next era of the game — under the SMG banner.
        </p>
      </Reveal>

      {athletes === null ? (
        <p className="mt-14 font-mono text-xs uppercase tracking-[0.3em] text-slate-500" data-testid="roster-gallery-loading">
          Loading roster…
        </p>
      ) : athletes.length === 0 ? (
        <Reveal delay={0.1}>
          <div
            className="mt-14 rounded-3xl border border-dashed border-white/15 bg-ink/60 p-12 text-center sm:p-16"
            data-testid="roster-gallery-empty-state"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-volt/10 text-volt">
              <Users className="h-7 w-7" />
            </span>
            <h3 className="mt-6 font-display text-2xl uppercase text-white">
              First signings announced soon.
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
              We're in the room with players right now. If you belong on this
              page, the door is open.
            </p>
            <a
              href="#apply"
              data-testid="roster-empty-apply-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-volt px-8 py-3.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
            >
              Apply for Representation
            </a>
          </div>
        </Reveal>
      ) : (
        <div
          className={`mt-14 grid gap-6 sm:grid-cols-2 ${athletes.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}
          data-testid="roster-gallery-grid"
        >
          {athletes.map((a, i) => (
            <AthleteCard key={a.id} athlete={a} index={i} />
          ))}
        </div>
      )}
    </SectionShell>
  );
}
